import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Box,
  Button,
  Center,
  Checkbox,
  Collapse,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  IconButton,
  Image,
  Input,
  Select,
  SimpleGrid,
  Switch,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Textarea,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useToast,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import {
  FaArrowLeft,
  FaChevronDown,
  FaChevronUp,
  FaPlus,
  FaTimes,
  FaTrash,
} from 'react-icons/fa';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { Link, useHistory } from 'react-router-dom';
import { SidebarWithHeader } from '../components';
import { useProductContext } from '../context/product_context';
import { categories_url } from '../utils/constants';
import { getSecureUrl } from '../utils/helpers';

function ColorPickerModal({ isOpen, onClose, initialHex, onSave }) {
  const [hex, setHex] = useState(initialHex || '#FFFFFF');

  useEffect(() => {
    if (isOpen) {
      setHex(initialHex || '#FFFFFF');
    }
  }, [isOpen, initialHex]);

  const hexToRgb = (value) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(value);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : { r: 255, g: 255, b: 255 };
  };

  const rgbToHex = (r, g, b) =>
    '#' +
    [r, g, b]
      .map((channel) =>
        Math.max(0, Math.min(255, Number(channel) || 0))
          .toString(16)
          .padStart(2, '0')
      )
      .join('');

  const rgb = hexToRgb(hex);

  const presetColors = [
    '#FF0000',
    '#FF6600',
    '#FF9900',
    '#FFCC00',
    '#996633',
    '#33CC33',
    '#339933',
    '#9933FF',
    '#CC33CC',
    '#6666FF',
    '#0099FF',
    '#00CCCC',
    '#33FF99',
    '#000000',
    '#333333',
    '#808080',
    '#C0C0C0',
    '#FFFFFF',
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size='sm'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize='md'>Pick color</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={5}>
          <VStack spacing={4} align='stretch'>
            <Box borderRadius='lg' overflow='hidden' border='1px solid' borderColor='gray.200'>
              <Input
                type='color'
                value={hex}
                onChange={(event) => setHex(event.target.value)}
                w='full'
                h='180px'
                p={0}
                border='none'
                cursor='pointer'
              />
            </Box>

            <HStack spacing={3}>
              <Box>
                <Text fontSize='xs' color='gray.500' mb={1}>Hex</Text>
                <Input
                  size='sm'
                  value={hex.replace('#', '').toUpperCase()}
                  onChange={(event) => {
                    const value = event.target.value.replace('#', '');
                    if (/^[0-9A-Fa-f]{0,6}$/.test(value) && value.length === 6) {
                      setHex('#' + value);
                    }
                  }}
                  w='95px'
                />
              </Box>
              <Box>
                <Text fontSize='xs' color='gray.500' mb={1}>R</Text>
                <Input
                  size='sm'
                  w='55px'
                  type='number'
                  min={0}
                  max={255}
                  value={rgb.r}
                  onChange={(event) => setHex(rgbToHex(event.target.value, rgb.g, rgb.b))}
                />
              </Box>
              <Box>
                <Text fontSize='xs' color='gray.500' mb={1}>G</Text>
                <Input
                  size='sm'
                  w='55px'
                  type='number'
                  min={0}
                  max={255}
                  value={rgb.g}
                  onChange={(event) => setHex(rgbToHex(rgb.r, event.target.value, rgb.b))}
                />
              </Box>
              <Box>
                <Text fontSize='xs' color='gray.500' mb={1}>B</Text>
                <Input
                  size='sm'
                  w='55px'
                  type='number'
                  min={0}
                  max={255}
                  value={rgb.b}
                  onChange={(event) => setHex(rgbToHex(rgb.r, rgb.g, event.target.value))}
                />
              </Box>
            </HStack>

            <Wrap spacing={2}>
              {presetColors.map((color) => (
                <WrapItem key={color}>
                  <Box
                    w='24px'
                    h='24px'
                    borderRadius='full'
                    bg={color}
                    border='2px solid'
                    borderColor={hex.toLowerCase() === color.toLowerCase() ? 'pink.500' : 'gray.200'}
                    cursor='pointer'
                    onClick={() => setHex(color)}
                    _hover={{ transform: 'scale(1.12)' }}
                    transition='all 0.15s'
                  />
                </WrapItem>
              ))}
            </Wrap>

            <Flex justify='flex-end'>
              <Button
                colorScheme='pink'
                onClick={() => {
                  onSave(hex);
                  onClose();
                }}
              >
                Save
              </Button>
            </Flex>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

function AddNewProductPage() {
  const history = useHistory();
  const toast = useToast();
  const { createNewProduct } = useProductContext();
  const addSpecification = () => setSpecifications([...specifications, { name: "", value: "" }]);
  const updateSpecification = (index, field, value) => { const newSpecs = [...specifications]; newSpecs[index][field] = value; setSpecifications(newSpecs); };
  const removeSpecification = (index) => setSpecifications(specifications.filter((_, i) => i !== index));

  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [stock, setStock] = useState(10);
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [shopName, setShopName] = useState('');
  const [shipping, setShipping] = useState(true);
  const [featured, setFeatured] = useState(false);
  const [isOffer, setIsOffer] = useState(false);
  const [originalPrice, setOriginalPrice] = useState('');
  const [slug, setSlug] = useState('');
  const [imageList, setImageList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showNewCategoryForm, setShowNewCategoryForm] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryImage, setNewCategoryImage] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);
  const [specifications, setSpecifications] = useState([]);
  const [dimensions, setDimensions] = useState({
    longueur: '',
    largeur: '',
    hauteur: '',
  });
  const [variantsOpen, setVariantsOpen] = useState(true);
  const [offersOpen, setOffersOpen] = useState(false);
  const [seoOpen, setSeoOpen] = useState(false);
  const [variants, setVariants] = useState([
    { name: '', type: 'dropdown', values: [], currentValue: '' },
  ]);
  const [offers, setOffers] = useState([{ description: '', price: '', shippingPrice: '' }]);
  const [seo, setSeo] = useState({
    metaTitle: '',
    metaDescription: '',
  });
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [editingVariantIndex, setEditingVariantIndex] = useState(null);
  const [editingValueIndex, setEditingValueIndex] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (name && !slug) {
      const generated = name
        .toLowerCase()
        .replace(/[àáâãäå]/g, 'a')
        .replace(/[èéêë]/g, 'e')
        .replace(/[ìíîï]/g, 'i')
        .replace(/[òóôõö]/g, 'o')
        .replace(/[ùúûü]/g, 'u')
        .replace(/[ç]/g, 'c')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setSlug(generated);
    }
  }, [name, slug]);

  const discountPercentage = useMemo(() => {
    const current = Number(price) || 0;
    const original = Number(originalPrice) || 0;
    if (!isOffer || !current || !original || original <= current) {
      return 0;
    }
    return Math.round((1 - current / original) * 100);
  }, [isOffer, price, originalPrice]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(categories_url);
      const cats = (response.data.data || []).map((cat) => ({
        ...cat,
        image: cat.image
          ? { ...cat.image, url: getSecureUrl(cat.image.url) }
          : cat.image,
      }));
      setCategories(cats);
    } catch (error) {
      setCategories([]);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        setImageList((previous) => [...previous, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const onCategoryImageDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        setNewCategoryImage(reader.result);
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/jpeg, image/png, image/gif',
    multiple: true,
  });

  const {
    getRootProps: getCategoryRootProps,
    getInputProps: getCategoryInputProps,
    isDragActive: isCategoryDragActive,
  } = useDropzone({
    onDrop: onCategoryImageDrop,
    accept: 'image/jpeg, image/png',
    multiple: false,
  });

  const removeImage = (index) => {
    setImageList((previous) => previous.filter((_, i) => i !== index));
  };

  const handleAddNewCategory = async () => {
    if (!newCategoryName || !newCategoryImage) {
      toast({
        position: 'top',
        description: 'Veuillez fournir le nom et l\'image de la catégorie',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setSavingCategory(true);
    try {
      await axios.post(
        `${categories_url}/admin`,
        { name: newCategoryName, image: newCategoryImage },
        {
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('acces_token'),
          },
        }
      );

      toast({
        position: 'top',
        description: 'Catégorie créée avec succès',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      await fetchCategories();
      setCategory(newCategoryName.toLowerCase());
      setShowNewCategoryForm(false);
      setNewCategoryName('');
      setNewCategoryImage('');
    } catch (error) {
      toast({
        position: 'top',
        description: error.response?.data?.message || 'Erreur lors de la création',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSavingCategory(false);
    }
  };

  const updateVariant = (index, field, value) => {
    setVariants((previous) =>
      previous.map((variant, i) =>
        i === index ? { ...variant, [field]: value } : variant
      )
    );
  };

  const addVariantRow = () => {
    setVariants((previous) => [
      ...previous,
      { name: '', type: 'dropdown', values: [], currentValue: '' },
    ]);
  };

  const removeVariantRow = (index) => {
    setVariants((previous) => previous.filter((_, i) => i !== index));
  };

  const addVariantValue = (index, rawValue) => {
    const value = rawValue.trim().replace(/,$/, '');
    if (!value) {
      return;
    }

    setVariants((previous) =>
      previous.map((variant, i) => {
        if (i !== index) {
          return variant;
        }

        const alreadyExists = variant.values.some((existingValue) => {
          if (typeof existingValue === 'string') {
            return existingValue.toLowerCase() === value.toLowerCase();
          }
          return existingValue.name.toLowerCase() === value.toLowerCase();
        });

        if (alreadyExists) {
          return { ...variant, currentValue: '' };
        }

        const nextValue =
          variant.type === 'color_buttons' ? { name: value, hex: '#FFFFFF' } : value;

        return {
          ...variant,
          values: [...variant.values, nextValue],
          currentValue: '',
        };
      })
    );
  };

  const removeVariantValue = (variantIndex, valueIndex) => {
    setVariants((previous) =>
      previous.map((variant, i) => {
        if (i !== variantIndex) {
          return variant;
        }
        return {
          ...variant,
          values: variant.values.filter((_, idx) => idx !== valueIndex),
        };
      })
    );
  };

  const handleVariantKeyDown = (index, event) => {
    if (event.key !== 'Enter' && event.key !== ',') {
      return;
    }

    event.preventDefault();
    addVariantValue(index, variants[index].currentValue);
  };

  const openColorPicker = (variantIndex, valueIndex) => {
    setEditingVariantIndex(variantIndex);
    setEditingValueIndex(valueIndex);
    setColorPickerOpen(true);
  };

  const getEditingHex = () => {
    if (editingVariantIndex === null || editingValueIndex === null) {
      return '#FFFFFF';
    }

    const variant = variants[editingVariantIndex];
    if (!variant || !variant.values[editingValueIndex]) {
      return '#FFFFFF';
    }

    const value = variant.values[editingValueIndex];
    if (typeof value === 'object' && value.hex) {
      return value.hex;
    }

    return '#FFFFFF';
  };

  const updateVariantColor = (hex) => {
    if (editingVariantIndex === null || editingValueIndex === null) {
      return;
    }

    setVariants((previous) =>
      previous.map((variant, variantIndex) => {
        if (variantIndex !== editingVariantIndex) {
          return variant;
        }

        return {
          ...variant,
          values: variant.values.map((value, valueIndex) => {
            if (valueIndex !== editingValueIndex) {
              return value;
            }

            if (typeof value === 'object') {
              return { ...value, hex };
            }

            return { name: value, hex };
          }),
        };
      })
    );
  };

  const updateOffer = (index, field, value) => {
    setOffers((previous) =>
      previous.map((offer, i) => (i === index ? { ...offer, [field]: value } : offer))
    );
  };

  const addOfferRow = () => {
    setOffers((previous) => [...previous, { description: '', price: '', shippingPrice: '' }]);
  };

  const removeOfferRow = (index) => {
    setOffers((previous) => previous.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    const { longueur, largeur, hauteur } = dimensions;
    const colorsFromVariants = variants
      .filter((variant) => variant.type === 'color_buttons')
      .flatMap((variant) =>
        variant.values.map((value) => {
          if (typeof value === 'object') {
            return value.hex || value.name;
          }
          return value;
        })
      )
      .filter(Boolean);

    if (
      !name ||
      !price ||
      !stock ||
      !category ||
      !specifications
    ) {
      toast({
        position: 'top',
        description:
          'Veuillez remplir tous les champs obligatoires (Nom, Prix, Stock, Catégorie, Specifications)',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (colorsFromVariants.length < 1) {
      toast({
        position: 'top',
        description:
          'Ajoutez au moins une couleur dans Variants avec le type Color based buttons',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (imageList.length < 1) {
      toast({
        position: 'top',
        description: 'Ajoutez au moins une image',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);

    const normalizedVariants = variants
      .filter((variant) => variant.name.trim() && variant.values.length > 0)
      .map((variant) => ({
        name: variant.name.trim(),
        type: variant.type,
        values: variant.values.map((value) => {
          if (typeof value === 'object') {
            return value;
          }
          return value;
        }),
      }));

    const normalizedOffers = offers
      .map((offer, index) => {
        const parsedPrice = Number(offer.price);
        if (!(parsedPrice > 0)) {
          return null;
        }

        const parsedShipping = Number(offer.shippingPrice);
        return {
          description: offer.description.trim() || `Offre ${index + 1}`,
          price: parsedPrice,
          shippingPrice: parsedShipping > 0 ? parsedShipping : 0,
        };
      })
      .filter(Boolean);

    const parsedPrice = Number(price) || 0;
    const parsedOriginalPrice = Number(originalPrice) || 0;
    const highestOfferPrice = normalizedOffers.length
      ? Math.max(...normalizedOffers.map((offer) => Number(offer.price) || 0))
      : 0;

    const normalizedOriginalPrice =
      parsedOriginalPrice > parsedPrice
        ? parsedOriginalPrice
        : highestOfferPrice > parsedPrice
        ? highestOfferPrice
        : null;

    const normalizedIsOffer = Boolean(
      isOffer || normalizedOffers.length > 0 || normalizedOriginalPrice
    );

    const normalizedDiscountPercentage =
      normalizedOriginalPrice && normalizedOriginalPrice > parsedPrice && parsedPrice > 0
        ? Math.round((1 - parsedPrice / normalizedOriginalPrice) * 100)
        : 0;

    const product = {
      name,
      price: parsedPrice,
      stock: Number(stock),
      description: description || '',
      category,
      specifications,
      colors: colorsFromVariants,
      shipping,
      featured,
      images: imageList,
      isOffer: normalizedIsOffer,
      originalPrice: normalizedOriginalPrice,
      discountPercentage: normalizedDiscountPercentage,
      variants: normalizedVariants,
      offers: normalizedOffers,
      seo: {
        slug,
        metaTitle: seo.metaTitle,
        metaDescription: seo.metaDescription,
      },
    };

    const result = await createNewProduct(product);
    setLoading(false);

    if (!result?.success) {
      toast({
        position: 'top',
        description: result?.message || 'Échec de création du produit',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    toast({
      position: 'top',
      description: 'Produit créé avec succès',
      status: 'success',
      duration: 4000,
      isClosable: true,
    });

    history.push('/products');
  };

  return (
    <SidebarWithHeader>
      <Flex align='center' mb={6}>
        <Button
          as={Link}
          to='/products'
          variant='ghost'
          leftIcon={<FaArrowLeft />}
          mr={4}
          size='sm'
        >
          Retour
        </Button>
        <Heading size='lg'>Créer un produit</Heading>
      </Flex>

      <Box mb={6}>
        <HStack
          bg='white'
          border='1px solid'
          borderColor='gray.200'
          borderRadius='lg'
          overflow='hidden'
        >
          <Box bg='gray.100' px={4} py={2.5} borderRight='1px solid' borderColor='gray.200'>
            <Text fontSize='sm' color='gray.500' whiteSpace='nowrap'>
              https://artbois.tn/products/
            </Text>
          </Box>
          <Input
            variant='unstyled'
            placeholder='Slug'
            value={slug}
            onChange={(event) => setSlug(event.target.value)}
            px={3}
            fontSize='sm'
            color='brown.500'
          />
        </HStack>
      </Box>

      <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={6}>
        <GridItem>
          <VStack spacing={6} align='stretch'>
            <Box bg='white' p={6} borderRadius='xl' border='1px solid' borderColor='gray.200'>
              <FormControl isRequired>
                <FormLabel fontWeight='bold'>Nom du produit</FormLabel>
                <Input
                  placeholder='Nom du produit'
                  size='lg'
                  focusBorderColor='brown.500'
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </FormControl>
            </Box>

            <Box bg='white' p={6} borderRadius='xl' border='1px solid' borderColor='gray.200'>
              <FormControl>
                <FormLabel fontWeight='bold'>Description</FormLabel>
                <Box borderRadius='md' border='1px solid' borderColor='gray.200' mb={12}>
                  <ReactQuill
                    theme='snow'
                    value={description}
                    onChange={setDescription}
                    style={{ height: '240px' }}
                    modules={{
                      toolbar: [
                        [{ size: ['small', false, 'large', 'huge'] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ color: [] }, { background: [] }],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                        [{ align: [] }],
                        ['link'],
                        ['clean'],
                      ],
                    }}
                  />
                </Box>
              </FormControl>
            </Box>

            <Box bg='white' p={6} borderRadius='xl' border='1px solid' borderColor='gray.200'>
              <Heading size='sm' mb={4}>
                Médias
              </Heading>
              <FormControl>
                <FormLabel fontSize='sm'>Images</FormLabel>
                <Center
                  bg='gray.50'
                  minHeight={100}
                  borderWidth={3}
                  borderColor='gray.200'
                  borderStyle='dashed'
                  borderRadius='lg'
                  cursor='pointer'
                  _hover={{ borderColor: 'brown.300', bg: 'brown.50' }}
                  transition='all 0.2s'
                  {...getRootProps()}
                >
                  <Input {...getInputProps()} />
                  {isDragActive ? (
                    <Text color='brown.500' fontWeight='bold'>
                      Déposez vos images ici
                    </Text>
                  ) : (
                    <VStack spacing={1}>
                      <Text fontSize='2xl'>
                        <span role='img' aria-label='camera'>📷</span>
                      </Text>
                      <Text fontSize='sm' color='gray.500'>
                        Glissez-déposez des images ou cliquez
                      </Text>
                      <Text fontSize='xs' color='gray.400'>
                        JPEG, PNG, GIF
                      </Text>
                    </VStack>
                  )}
                </Center>
              </FormControl>

              <Wrap spacing={3} mt={3}>
                {imageList.map((image, index) => (
                  <WrapItem key={index}>
                    <Box position='relative'>
                      <Image src={image} boxSize='100px' objectFit='cover' borderRadius='lg' />
                      <IconButton
                        icon={<FaTimes />}
                        size='xs'
                        colorScheme='red'
                        position='absolute'
                        top={1}
                        right={1}
                        onClick={() => removeImage(index)}
                        aria-label='Remove image'
                      />
                    </Box>
                  </WrapItem>
                ))}
              </Wrap>
            </Box>

            <Box bg='white' borderRadius='xl' border='1px solid' borderColor='gray.200' overflow='hidden'>
              <Flex
                align='center'
                justify='space-between'
                px={6}
                py={4}
                cursor='pointer'
                onClick={() => setVariantsOpen(!variantsOpen)}
                _hover={{ bg: 'gray.50' }}
              >
                <HStack>
                  {variantsOpen ? <FaChevronUp /> : <FaChevronDown />}
                  <Heading size='sm'>Variants</Heading>
                </HStack>
                <Switch
                  colorScheme='blue'
                  isChecked={variantsOpen}
                  onChange={(event) => {
                    event.stopPropagation();
                    setVariantsOpen(event.target.checked);
                  }}
                />
              </Flex>
              <Collapse in={variantsOpen} animateOpacity>
                <Box px={6} pb={6}>
                  {variants.map((variant, index) => (
                    <Grid key={index} templateColumns='1fr 1fr 2fr auto' gap={3} mb={3}>
                      <FormControl>
                        <FormLabel fontSize='sm'>Option</FormLabel>
                        <Input
                          size='sm'
                          placeholder='ex: material, style'
                          value={variant.name}
                          onChange={(event) =>
                            updateVariant(index, 'name', event.target.value)
                          }
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize='sm'>Type</FormLabel>
                        <Select
                          size='sm'
                          value={variant.type}
                          onChange={(event) =>
                            updateVariant(index, 'type', event.target.value)
                          }
                        >
                          <option value='dropdown'>Dropdown list</option>
                          <option value='textual_buttons'>Textual buttons</option>
                          <option value='color_buttons'>Color based buttons</option>
                          <option value='radio_buttons'>Radio buttons</option>
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize='sm'>Value</FormLabel>
                        <Wrap mb={2}>
                          {variant.values.map((valueItem, valueIndex) => (
                            <WrapItem
                              key={`${
                                typeof valueItem === 'object' ? valueItem.name : valueItem
                              }-${valueIndex}`}
                            >
                              <Tag size='sm' colorScheme='blue' borderRadius='full'>
                                {variant.type === 'color_buttons' && (
                                  <Box
                                    w='12px'
                                    h='12px'
                                    borderRadius='full'
                                    bg={
                                      typeof valueItem === 'object'
                                        ? valueItem.hex || '#FFFFFF'
                                        : '#FFFFFF'
                                    }
                                    border='2px solid'
                                    borderColor='gray.300'
                                    mr={1}
                                    cursor='pointer'
                                    onClick={() => openColorPicker(index, valueIndex)}
                                  />
                                )}
                                <TagLabel>
                                  {typeof valueItem === 'object' ? valueItem.name : valueItem}
                                </TagLabel>
                                <TagCloseButton
                                  onClick={() => removeVariantValue(index, valueIndex)}
                                />
                              </Tag>
                            </WrapItem>
                          ))}
                        </Wrap>
                        <Input
                          size='sm'
                          placeholder={
                            variant.type === 'color_buttons'
                              ? 'Type color name, then ↵'
                              : 'Type value then press Enter or ,'
                          }
                          value={variant.currentValue}
                          onKeyDown={(event) => handleVariantKeyDown(index, event)}
                          onChange={(event) =>
                            updateVariant(index, 'currentValue', event.target.value)
                          }
                        />
                      </FormControl>
                      <Flex align='end'>
                        <IconButton
                          icon={<FaTrash />}
                          size='sm'
                          variant='ghost'
                          colorScheme='red'
                          onClick={() => removeVariantRow(index)}
                          aria-label='Remove variant row'
                        />
                      </Flex>
                    </Grid>
                  ))}
                  <Button
                    variant='outline'
                    colorScheme='pink'
                    size='sm'
                    leftIcon={<FaPlus />}
                    onClick={addVariantRow}
                    w='full'
                  >
                    Add an option
                  </Button>
                </Box>
              </Collapse>
            </Box>

            <Box bg='white' borderRadius='xl' border='1px solid' borderColor='gray.200' overflow='hidden'>
              <Flex
                align='center'
                justify='space-between'
                px={6}
                py={4}
                cursor='pointer'
                onClick={() => setOffersOpen(!offersOpen)}
                _hover={{ bg: 'gray.50' }}
              >
                <HStack>
                  {offersOpen ? <FaChevronUp /> : <FaChevronDown />}
                  <Heading size='sm'>Offres</Heading>
                </HStack>
                <Switch
                  colorScheme='green'
                  isChecked={offersOpen}
                  onChange={(event) => {
                    event.stopPropagation();
                    setOffersOpen(event.target.checked);
                  }}
                />
              </Flex>
              <Collapse in={offersOpen} animateOpacity>
                <Box px={6} pb={6}>
                  {offers.map((offer, index) => (
                    <Grid key={index} templateColumns='2fr 1fr 1fr auto' gap={3} mb={3}>
                      <FormControl>
                        <FormLabel fontSize='sm'>Description de l'offre</FormLabel>
                        <Input
                          size='sm'
                          placeholder='Pack 2 pièces, Offre spéciale...'
                          value={offer.description}
                          onChange={(event) =>
                            updateOffer(index, 'description', event.target.value)
                          }
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize='sm'>Prix (TND)</FormLabel>
                        <Input
                          size='sm'
                          type='number'
                          value={offer.price}
                          onChange={(event) => updateOffer(index, 'price', event.target.value)}
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize='sm'>Livraison (TND)</FormLabel>
                        <Input
                          size='sm'
                          type='number'
                          value={offer.shippingPrice}
                          onChange={(event) =>
                            updateOffer(index, 'shippingPrice', event.target.value)
                          }
                        />
                      </FormControl>
                      <Flex align='end'>
                        <IconButton
                          icon={<FaTrash />}
                          size='sm'
                          variant='ghost'
                          colorScheme='red'
                          onClick={() => removeOfferRow(index)}
                          aria-label='Remove offer row'
                        />
                      </Flex>
                    </Grid>
                  ))}
                  <Button
                    variant='outline'
                    colorScheme='green'
                    size='sm'
                    leftIcon={<FaPlus />}
                    onClick={addOfferRow}
                    w='full'
                  >
                    Ajouter une offre
                  </Button>
                </Box>
              </Collapse>
            </Box>

            <Box bg='white' borderRadius='xl' border='1px solid' borderColor='gray.200' overflow='hidden'>
              <Flex
                align='center'
                justify='space-between'
                px={6}
                py={4}
                cursor='pointer'
                onClick={() => setSeoOpen(!seoOpen)}
                _hover={{ bg: 'gray.50' }}
              >
                <HStack>
                  {seoOpen ? <FaChevronUp /> : <FaChevronDown />}
                  <Heading size='sm'>SEO</Heading>
                </HStack>
              </Flex>
              <Collapse in={seoOpen} animateOpacity>
                <Box px={6} pb={6}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb={3}>
                    <FormControl>
                      <FormLabel fontSize='sm'>Meta slug</FormLabel>
                      <Input size='sm' value={slug} onChange={(event) => setSlug(event.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize='sm'>Meta title</FormLabel>
                      <Input
                        size='sm'
                        value={seo.metaTitle}
                        onChange={(event) => {
                          const value = event.target.value;
                          setSeo((previous) => ({ ...previous, metaTitle: value }));
                        }}
                      />
                    </FormControl>
                  </SimpleGrid>
                  <FormControl>
                    <FormLabel fontSize='sm'>Meta description</FormLabel>
                    <Textarea
                      size='sm'
                      rows={4}
                      value={seo.metaDescription}
                      onChange={(event) => {
                        const value = event.target.value;
                        setSeo((previous) => ({ ...previous, metaDescription: value }));
                      }}
                    />
                  </FormControl>
                </Box>
              </Collapse>
            </Box>
          </VStack>
        </GridItem>

        <GridItem>
          <VStack spacing={4} align='stretch'>
            <Box bg='white' p={4} borderRadius='xl' border='1px solid' borderColor='gray.200'>
              <Heading size='sm' mb={3}>
                Tarification
              </Heading>
              <FormControl mb={3} isRequired>
                <FormLabel fontSize='sm'>Prix (TND)</FormLabel>
                <Input
                  type='number'
                  value={price}
                  onChange={(event) => setPrice(event.target.value)}
                />
              </FormControl>
              <Checkbox
                colorScheme='orange'
                isChecked={isOffer}
                onChange={(event) => setIsOffer(event.target.checked)}
                mb={3}
              >
                Produit en promotion
              </Checkbox>
              {isOffer && (
                <FormControl mb={3}>
                  <FormLabel fontSize='sm'>Prix original (TND)</FormLabel>
                  <Input
                    type='number'
                    value={originalPrice}
                    onChange={(event) => setOriginalPrice(event.target.value)}
                  />
                  <FormHelperText>
                    {discountPercentage > 0 ? `Remise: -${discountPercentage}%` : 'Entrez le prix original'}
                  </FormHelperText>
                </FormControl>
              )}
              <FormControl isRequired>
                <FormLabel fontSize='sm'>Stock</FormLabel>
                <Input
                  type='number'
                  value={stock}
                  onChange={(event) => setStock(event.target.value)}
                />
              </FormControl>
            </Box>

            <Box bg='white' p={4} borderRadius='xl' border='1px solid' borderColor='gray.200'>
              <Heading size='sm' mb={3}>
                Catégorie
              </Heading>
              <HStack align='start'>
                <Select
                  placeholder='Sélectionner une catégorie'
                  value={category}
                  onChange={(event) => setCategory(event.target.value)}
                >
                  {categories.map((cat) => (
                    <option key={cat._id} value={cat.name}>
                      {cat.name.charAt(0).toUpperCase() + cat.name.slice(1)}
                    </option>
                  ))}
                </Select>
                <IconButton
                  icon={<FaPlus />}
                  colorScheme='green'
                  aria-label='Add category'
                  onClick={() => setShowNewCategoryForm(!showNewCategoryForm)}
                />
              </HStack>

              <Collapse in={showNewCategoryForm} animateOpacity>
                <VStack mt={3} spacing={2} align='stretch'>
                  <Input
                    placeholder='Nom de catégorie'
                    value={newCategoryName}
                    onChange={(event) => setNewCategoryName(event.target.value)}
                    size='sm'
                  />
                  <Center
                    bg='gray.50'
                    minHeight={80}
                    borderWidth={2}
                    borderColor='gray.200'
                    borderStyle='dashed'
                    borderRadius='md'
                    cursor='pointer'
                    {...getCategoryRootProps()}
                  >
                    {newCategoryImage ? (
                      <Image src={newCategoryImage} alt='Category' maxH='70px' objectFit='contain' />
                    ) : isCategoryDragActive ? (
                      <Text fontSize='sm'>Drop image here</Text>
                    ) : (
                      <Text fontSize='sm'>Drop category image or click</Text>
                    )}
                  </Center>
                  <Input {...getCategoryInputProps()} />
                  <Button
                    size='sm'
                    colorScheme='green'
                    onClick={handleAddNewCategory}
                    isLoading={savingCategory}
                  >
                    Save Category
                  </Button>
                </VStack>
              </Collapse>
            </Box>

            <Box bg='white' p={4} borderRadius='xl' border='1px solid' borderColor='gray.200'>
              <Heading size='sm' mb={3}>
                Options
              </Heading>
              <VStack align='stretch' spacing={3}>
                <Checkbox
                  colorScheme='brown'
                  isChecked={shipping}
                  onChange={(event) => setShipping(event.target.checked)}
                >
                  Frais de livraison (7 TND)
                </Checkbox>
                <Checkbox
                  colorScheme='green'
                  isChecked={featured}
                  onChange={(event) => setFeatured(event.target.checked)}
                >
                  Produit vedette
                </Checkbox>
              </VStack>
            </Box>

            <Box bg='white' p={4} borderRadius='xl' border='1px solid' borderColor='gray.200'>
              <HStack justify='space-between' mb={4}>
                <Heading size='sm'>Spécifications</Heading>
                <Button size='sm' colorScheme='brown' variant='outline' onClick={addSpecification}>
                  + Ajouter
                </Button>
              </HStack>

              <VStack spacing={4} align='stretch'>
                {specifications.map((spec, index) => (
                  <HStack key={index} align='flex-end' spacing={3} p={3} borderWidth='1px' borderRadius='md' bg='gray.50'>
                    <FormControl>
                      <FormLabel fontSize='xs'>Clé (ex: Dimensions)</FormLabel>
                      <Input
                        bg='white'
                        value={spec.name}
                        onChange={(e) => updateSpecification(index, 'name', e.target.value)}
                        placeholder='Nom'
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize='xs'>Valeur</FormLabel>
                      <Input
                        bg='white'
                        value={spec.value}
                        onChange={(e) => updateSpecification(index, 'value', e.target.value)}
                        placeholder='Valeur'
                      />
                    </FormControl>
                    <IconButton
                      aria-label='Supprimer'
                      icon={<FaTrash />}
                      colorScheme='red'
                      variant='ghost'
                      onClick={() => removeSpecification(index)}
                    />
                  </HStack>
                ))}
                {specifications.length === 0 && (
                  <Text fontSize='sm' color='gray.500' textAlign='center' py={4}>
                    Aucune spécification ajoutée
                  </Text>
                )}
              </VStack>
            </Box>

            <HStack justify='flex-end'>
              <Button variant='outline' onClick={() => history.push('/products')}>
                Annuler
              </Button>
              <Button
                colorScheme='brown'
                isLoading={loading}
                loadingText='Création...'
                onClick={handleSubmit}
              >
                Enregistrer le produit
              </Button>
            </HStack>
          </VStack>
        </GridItem>
      </Grid>

      <ColorPickerModal
        isOpen={colorPickerOpen}
        onClose={() => {
          setColorPickerOpen(false);
          setEditingVariantIndex(null);
          setEditingValueIndex(null);
        }}
        initialHex={getEditingHex()}
        onSave={updateVariantColor}
      />
    </SidebarWithHeader>
  );
}

export default AddNewProductPage;